import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faComment } from '@fortawesome/free-solid-svg-icons';
import { faDiscord } from '@fortawesome/free-brands-svg-icons';

const AIChat: React.FC = () => {
  const [message, setMessage] = useState('');
  const [chatHistory, setChatHistory] = useState<Array<{ type: 'user' | 'system'; text: string }>>([
    { type: 'system', text: 'Send messages directly to your Discord server!' },
  ]);
  const [sending, setSending] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Auto scroll to bottom when chat history changes
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory]);

  const handleSendMessage = async () => {
    if (!message.trim()) return;

    const userMessage = message.trim();
    setMessage('');
    setSending(true);

    // Add user message to chat
    setChatHistory(prev => [...prev, { type: 'user', text: userMessage }]);

    try {
      // Send message to Discord via bot API
      const API_BASE_URL = process.env.REACT_APP_DISCORD_API_URL || 'http://localhost:3001';
      const response = await fetch(`${API_BASE_URL}/api/chat/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send message to Discord');
      }

      const data = await response.json();
      setChatHistory(prev => [
        ...prev,
        { type: 'system', text: `✅ Message sent to #${data.channelName || 'discord'}` },
      ]);
    } catch (error) {
      console.error('Error sending message:', error);
      setChatHistory(prev => [
        ...prev,
        { type: 'system', text: `❌ Error: ${error instanceof Error ? error.message : 'Unknown error'}` },
      ]);
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#9b5de5] to-[#00bbf9] p-8 lg:ml-64 lg:mr-80 flex flex-col">
      <div className="max-w-4xl mx-auto flex-1 flex flex-col">
        {/* Header */}
        <div className="bg-[#ffd803] border-8 border-black p-8 mb-8 shadow-[12px_12px_0px_0px_rgba(0,0,0,1)]">
          <h1 className="text-6xl font-black uppercase mb-4 flex items-center gap-4">
            <FontAwesomeIcon icon={faDiscord} className="text-[#5865F2]" />
            Discord Messenger
          </h1>
          <p className="text-xl font-bold mb-3">
            Send messages directly to your Discord server!
          </p>
          <div className="bg-[#5865F2] border-4 border-black p-4 text-white">
            <p className="font-bold">
              Messages appear as <span className="bg-white text-[#5865F2] px-2 py-1 border-2 border-black">@fuckedshit</span> in the <span className="bg-white text-[#5865F2] px-2 py-1 border-2 border-black">#only-shit-fucks</span> channel
            </p>
          </div>
        </div>

        {/* Chat Container */}
        <div className="bg-white border-8 border-black shadow-[12px_12px_0px_0px_rgba(0,0,0,1)] flex-1 flex flex-col">
          {/* Chat History */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-gray-50">
            {chatHistory.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-xs md:max-w-md p-4 border-4 border-black ${
                    msg.type === 'user' ? 'bg-[#00bbf9]' : 'bg-[#ffd803]'
                  }`}
                >
                  {msg.type === 'system' && (
                    <div className="flex items-center gap-2 mb-2">
                      <FontAwesomeIcon icon={faComment} className="text-gray-700" />
                      <span className="font-black text-sm">System</span>
                    </div>
                  )}
                  <p className="font-bold break-words">{msg.text}</p>
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-[#ffd803] border-4 border-black p-4">
                  <div className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faPaperPlane} className="text-[#9b5de5] animate-pulse" />
                    <span className="font-bold">Sending...</span>
                  </div>
                </div>
              </div>
            )}
            {/* Scroll anchor */}
            <div ref={chatEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-6 bg-white border-t-4 border-black">
            {/* Message Input */}
            <div className="flex gap-3">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type a message to send to Discord..."
                disabled={sending}
                rows={3}
                className="flex-1 p-4 border-4 border-black font-bold resize-none focus:outline-none focus:ring-4 focus:ring-[#9b5de5] disabled:bg-gray-100"
              />
              <button
                onClick={handleSendMessage}
                disabled={sending || !message.trim()}
                className="px-6 bg-[#9b5de5] border-4 border-black font-black uppercase transition-all hover:translate-x-1 hover:translate-y-1 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] active:shadow-none disabled:bg-gray-300 disabled:cursor-not-allowed text-white"
              >
                <FontAwesomeIcon icon={faPaperPlane} className="text-2xl" />
              </button>
            </div>

            <p className="mt-3 text-sm font-bold text-gray-600">
              Press Enter to send • Shift+Enter for new line
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIChat;
